import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { StoredCollectionSessionV2 } from '../session/collection-session-storage';
import { AuthService } from '../services/auth.service';
import { isExplicitSessionInvalidation } from '../session/session-invalidation-classifier';
import {
  COLLECTION_REQUEST_ID,
  CollectionRequestContextMissingError,
} from './collection-request-context';

const SESSION_INVALIDATION_CODES = new Set(['COLLECTION_SESSION_INVALID']);

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (bypassesCollectionAuthentication(request)) return next(request);

  const collectionId = request.context.get(COLLECTION_REQUEST_ID);
  if (!collectionId) {
    return throwError(() => new CollectionRequestContextMissingError());
  }

  const auth = inject(AuthService);
  return auth.runWithValidSession(collectionId, (snapshot) =>
    next(withCapturedSession(request, snapshot)).pipe(
      catchError((error: unknown) => {
        if (isTypedSessionInvalidation(error)) {
          return auth
            .invalidateCapturedSession(collectionId, snapshot.revision)
            .pipe(switchMap(() => throwError(() => error)));
        }
        if (!isGenericBearerUnauthorized(error)) {
          return throwError(() => error);
        }

        return auth.refreshAfterUnauthorized(
          collectionId,
          snapshot.revision,
          (refreshed) =>
            next(withCapturedSession(request, refreshed)).pipe(
              catchError((retryError: unknown) => {
                if (isTypedSessionInvalidation(retryError)) {
                  return auth
                    .invalidateCapturedSession(collectionId, refreshed.revision)
                    .pipe(switchMap(() => throwError(() => retryError)));
                }
                return throwError(() => retryError);
              }),
            ),
        );
      }),
    ),
  );
};

function withCapturedSession(
  request: HttpRequest<unknown>,
  snapshot: StoredCollectionSessionV2,
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${snapshot.access_token}`,
      'X-Session-ID': snapshot.session_id,
    },
  });
}

function bypassesCollectionAuthentication(
  request: HttpRequest<unknown>,
): boolean {
  const url = request.url;
  if (isDirectCollectionFileRequest(request)) return false;
  if (!/\/api\/chordium\/v1\//.test(url)) return true;
  if (/\/collections\/auth\/[^/]+\/verify-passcode(?:[/?#]|$)/.test(url)) {
    return true;
  }
  return /(?:amazonaws\.com|\.s3[.-])/.test(url);
}

function isDirectCollectionFileRequest(request: HttpRequest<unknown>): boolean {
  const prefix = `${environment.apiUrlLegacy}/api/authorization/chordium/v1/collections/`;
  if (!request.url.startsWith(prefix)) return false;
  const route = request.url
    .slice(prefix.length)
    .match(
      /^[^/?#]+\/revisions\/[^/?#]+\/targets\/[^/?#]+\/files\/([^/?#]+)$/,
    );
  if (!route) return false;
  const fileAction = route[1];
  return request.method === 'POST'
    ? fileAction === 'stage' || fileAction === 'validate'
    : request.method === 'DELETE' &&
        fileAction !== 'stage' &&
        fileAction !== 'validate';
}

function isTypedSessionInvalidation(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    isExplicitSessionInvalidation(error, SESSION_INVALIDATION_CODES)
  );
}

function isGenericBearerUnauthorized(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 401 &&
    structuredErrorCode(error.error) === 'UNAUTHORIZED'
  );
}

function structuredErrorCode(error: unknown): string | null {
  if (!isRecord(error) || !isRecord(error['error'])) return null;
  const code = error['error']['code'];
  return typeof code === 'string' ? code : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
